/**
 * Helpers de "collecte" d'événement (fundraising).
 *
 * Un événement peut porter une collecte de fonds optionnelle :
 *   event.fundraising = {
 *     enabled: boolean,
 *     budget: number | null,            // objectif/budget (optionnel)
 *     defaultAmount: number | null,     // montant attendu par défaut
 *     amountsByCategory: { [sassCategoryId]: number } // montant attendu par profil
 *   }
 *
 * Les profils sont les "catégories Sass" définies dans settings.sassCategories
 * et assignées aux membres via user.sassCategory.
 */

export const isFundraising = (event) => !!event?.fundraising?.enabled;

// Délai de grâce : un événement reste rattachable aux cotisations/dépenses
// jusqu'à N jours après sa date, puis disparaît automatiquement des sélecteurs.
export const EVENT_LINK_GRACE_DAYS = 7;

/**
 * Un événement est "ouvert aux cotisations" tant que sa date n'est pas dépassée
 * de plus de EVENT_LINK_GRACE_DAYS jours. Permet de proposer l'événement dans les
 * formulaires (cotisation, dépense) puis de le retirer automatiquement ~1 semaine
 * après son expiration, sans intervention manuelle.
 */
export const isEventLinkable = (event, now = new Date(), graceDays = EVENT_LINK_GRACE_DAYS) => {
  if (!event?.date) return false;
  const d = new Date(event.date);
  if (isNaN(d.getTime())) return false;
  return d.getTime() + graceDays * 24 * 60 * 60 * 1000 >= now.getTime();
};

/** Tri pratique pour les sélecteurs : à venir d'abord (le plus proche en tête), puis passés récents. */
export const sortForLinking = (events = [], now = new Date()) =>
  [...events].sort((a, b) => {
    const da = new Date(a.date).getTime();
    const dbb = new Date(b.date).getTime();
    const aUpcoming = da >= now.getTime();
    const bUpcoming = dbb >= now.getTime();
    if (aUpcoming !== bUpcoming) return aUpcoming ? -1 : 1; // à venir avant passés
    return aUpcoming ? da - dbb : dbb - da; // à venir : plus proche d'abord ; passés : plus récent d'abord
  });

/** Montant attendu pour un membre, selon son profil (catégorie Sass). */
export const expectedAmountForMember = (event, member) => {
  const f = event?.fundraising;
  if (!f?.enabled) return 0;
  const byCat = f.amountsByCategory || {};
  const catId = member?.sassCategory;
  if (catId && byCat[catId] != null && byCat[catId] !== '') {
    return Number(byCat[catId]) || 0;
  }
  return Number(f.defaultAmount) || 0;
};

/** Somme des montants attendus sur l'ensemble des membres fournis. */
export const expectedTotal = (event, members = []) =>
  members.reduce((s, m) => s + expectedAmountForMember(event, m), 0);

/** Total réellement encaissé (cotisations CONFIRMÉES affectées à l'événement). */
export const collectedTotal = (eventId, confirmedContributions = []) =>
  confirmedContributions
    .filter((c) => c.eventId === eventId)
    .reduce((s, c) => s + (c.amount || 0), 0);

/** Total des dépenses liées à l'événement. */
export const expensesTotal = (eventId, expenses = []) =>
  expenses
    .filter((e) => e.eventId === eventId)
    .reduce((s, e) => s + (e.amount || 0), 0);

/** Versé par un membre pour un événement (cotisations confirmées). */
export const memberPaidForEvent = (eventId, userId, confirmedContributions = []) =>
  confirmedContributions
    .filter((c) => c.eventId === eventId && c.userId === userId)
    .reduce((s, c) => s + (c.amount || 0), 0);
